/**
 * `/api/license-leases` wrappers.
 *
 * Two shapes of endpoint behind one prefix: the aggregate reads
 * (`daily-rewards/:owner`, `network-stats`) hit our own database, while
 * `leases/:owner`, `licenses/:owner/periods` and `request-new-lease` are proxies
 * to the upstream lease service — which is why every upstream field is optional
 * (see the shared schema's header).
 */
import type {
  CreateLeaseResponse,
  GetLeasesResponse,
  LeaseDailyRewardsResponse,
  LeaseNetworkStatsResponse,
  LicensesByPeriodResponse,
  RequestNewLeaseRequest,
} from "@swarm/shared/schemas";
import {
  createLeaseResponseSchema,
  getLeasesResponseSchema,
  leaseDailyRewardsResponseSchema,
  leaseNetworkStatsResponseSchema,
  licensesByPeriodResponseSchema,
} from "@swarm/shared/schemas";

import { routes } from "@/hooks/useRoutes/useRoutes";

import { apiRequest } from "./client";

/** Every lease owned by `owner`, with the upstream's own totals. */
export const getLeasesByOwner = (owner: string): Promise<GetLeasesResponse> =>
  apiRequest(routes.api.licenseLeases.getLeasesByOwner(owner), getLeasesResponseSchema);

/** Licenses owned by `owner`, split by lease duration, plus the unleased ones. */
export const getLicensesByOwner = (owner: string): Promise<LicensesByPeriodResponse> =>
  apiRequest(routes.api.licenseLeases.getLicensesByOwner(owner), licensesByPeriodResponseSchema);

/**
 * Daily leasing distributions for `owner`.
 *
 * `days` is clamped to 1..365 by the API — it reaches a Postgres INTERVAL
 * literal, where a NaN was a 500 and a huge value an interval overflow.
 */
export const getLeaseDailyRewards = (
  owner: string,
  days?: number,
): Promise<LeaseDailyRewardsResponse> =>
  apiRequest(
    routes.api.licenseLeases.getDailyRewards(owner, days),
    leaseDailyRewardsResponseSchema,
  );

/** Projected $TRUTH per license over each term, at current pool occupancy. */
export const getLeaseNetworkStats = (): Promise<LeaseNetworkStatsResponse> =>
  apiRequest(routes.api.licenseLeases.networkStats(), leaseNetworkStatsResponseSchema);

/**
 * Requests a new lease.
 *
 * Only `owner`, `quantity` and `duration_months` are forwarded upstream; the
 * signature envelope is consumed by our API and deliberately dropped, because
 * the upstream call carries a privileged API key.
 */
export const requestNewLease = (body: RequestNewLeaseRequest): Promise<CreateLeaseResponse> =>
  apiRequest(routes.api.licenseLeases.requestLeaseCreation(), createLeaseResponseSchema, {
    method: "POST",
    body,
  });
